/**
 * Is this router error a route chunk that could not be loaded?
 *
 * A lazily-imported route lives in its own hashed chunk. After a deploy the
 * running tab still holds the OLD chunk names, and Vercel answers an unknown
 * `/assets/*.js` with the SPA fallback — `200 text/html` — rather than a 404.
 * So the browser never reports a fetch failure: it rejects the module on MIME
 * grounds, and vue-router surfaces the wrapped rejection as
 * `Couldn't resolve component "default" at "/admin"`.
 *
 * That message was NOT in the original list, which only knew the fetch-level
 * wordings. On this host the fetch-level wordings are the ones that never
 * fire — so recovery never fired either, and a tab one deploy behind simply
 * abandoned the navigation in silence. Tapping "Platform admin" in Settings
 * on 2026-09-08 did nothing at all for exactly this reason: reproduced on
 * staging by serving the admin chunk as the SPA fallback, the URL never
 * changed and the settings overlay stayed open.
 *
 * Matching is on the message text because that is all vue-router gives us.
 * A false positive costs one reload of the page the user was already asking
 * for, guarded to once per path per session by the caller; a false negative
 * costs a dead door with no way out.
 */
const CHUNK_LOAD_FAILURE_PATTERNS = [
  // Chrome / Firefox / Safari, fetch level — a genuine 404 or offline
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  // Chrome, MIME level — the SPA fallback served HTML for a missing chunk
  'failed to load module script',
  'expected a javascript-or-wasm module script',
  // Safari, same cause, its own wording
  'is not a valid javascript mime type',
  // vue-router's own wrapper around any of the above, and the one this host
  // actually produces
  "couldn't resolve component",
]

export function isChunkLoadError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase()
  return CHUNK_LOAD_FAILURE_PATTERNS.some((p) => msg.includes(p))
}

/**
 * THE BLANK PAGE (job #217, Tom 2026-10-21): Settings > Platform admin on
 * staging, Safari private window, ended on a blank canvas with only the Back
 * pill. Reproduced by serving the admin chunk as the SPA fallback AFTER the
 * one plain reload: the reload is answered by the old service worker's
 * precached shell (NetworkFirst gives up after 3s, and the admin chunks are
 * deliberately not precached), so it names the same dead chunks, the guard
 * stops a second plain reload, and the failed FIRST navigation leaves no route
 * at all. Nothing on screen, no way forward.
 *
 * So recovery has three rungs, once each per path per session:
 *   1. reload the path (a fresh index.html, if the network answers);
 *   2. unregister the service workers and delete the caches, then reload — the
 *      only thing that cannot be answered by a stale shell. Sign-in lives in
 *      storage, which this leaves alone;
 *   3. say so in words, with a button that does rung 2, instead of nothing.
 */
export interface ChunkRecoveryDeps {
  read: (key: string) => string | null
  write: (key: string, value: string) => void
  purge: () => Promise<void>
  go: (path: string) => void
  say: (path: string) => void
}

/** Cleanup may hang (a service worker or cache call that never settles); the page must still move on. */
export const CHUNK_CLEANUP_DEADLINE_MS = 4000
export async function boundedCleanup(cleanup: () => Promise<void>, ms = CHUNK_CLEANUP_DEADLINE_MS): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([cleanup(), new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })])
  } finally {
    clearTimeout(timer)
  }
}

export type ChunkRecoveryStep = 'reload' | 'purge-and-reload' | 'say'

export async function recoverFromChunkError(fullPath: string, deps: ChunkRecoveryDeps): Promise<ChunkRecoveryStep> {
  const key = 'ssi-chunk-reload:' + fullPath
  let tried: string | null = null
  try { tried = deps.read(key) } catch { /* storage blocked */ }
  if (tried === '2') {
    deps.say(fullPath)
    return 'say'
  }
  if (tried === '1') {
    try { deps.write(key, '2') } catch { /* storage blocked */ }
    try { await boundedCleanup(deps.purge) } catch { /* reload anyway */ }
    deps.go(fullPath)
    return 'purge-and-reload'
  }
  try { deps.write(key, '1') } catch { /* storage blocked */ }
  deps.go(fullPath)
  return 'reload'
}
